import { getConfig } from '../location-services';
/**
 * Get API key from global config or prop override
 */
export function getApiKey(override?: string): string {
	const config = getConfig();
	const apiKey = override || config?.apiKey;
	if (!apiKey) {
		throw new Error(
			'Google Maps API key not configured. Call configure({ apiKey: "your-key" }) first.',
		);
	}
	return apiKey;
}

/**
 * Create custom location marker icon using SVG
 */
export function createLocationIcon(): string {
	const svgIcon = `
		<svg width="41" height="40" viewBox="0 0 41 40" fill="none" xmlns="http://www.w3.org/2000/svg">
<g clip-path="url(#clip0_3060_59906)">
<path fill-rule="evenodd" clip-rule="evenodd" d="M20.4997 1.85156C27.5288 1.85156 33.227 7.54976 33.227 14.5788C33.227 15.4368 33.1421 16.275 32.9803 17.0854C31.8569 25.5505 20.5666 35.1849 20.5666 35.1849C20.5666 35.1849 10.9585 26.9859 8.58735 19.0695C8.06065 17.673 7.77246 16.1596 7.77246 14.5788C7.77246 7.54976 13.4707 1.85156 20.4997 1.85156Z" fill="black"/>
<path d="M25.3478 14.5809C25.3478 11.9032 23.1771 9.73242 20.4994 9.73242C17.8216 9.73242 15.6509 11.9032 15.6509 14.5809C15.6509 17.2587 17.8216 19.4294 20.4994 19.4294C23.1771 19.4294 25.3478 17.2587 25.3478 14.5809Z" fill="white"/>
<g filter="url(#filter0_f_3060_59906)">
<ellipse cx="21.1012" cy="34.4434" rx="14.1667" ry="5" fill="black" fill-opacity="0.2"/>
</g>
</g>
<defs>
<filter id="filter0_f_3060_59906" x="4.93457" y="27.4434" width="32.3335" height="14" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
<feFlood flood-opacity="0" result="BackgroundImageFix"/>
<feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"/>
<feGaussianBlur stdDeviation="1" result="effect1_foregroundBlur_3060_59906"/>
</filter>
<clipPath id="clip0_3060_59906">
<rect width="40" height="40" fill="white" transform="translate(0.5)"/>
</clipPath>
</defs>
</svg>

	`;
	return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgIcon)}`;
}

/**
 * Injects the Google Maps JS API once and wires up the familiar
 * `google.maps.importLibrary()` method (with pre-load queuing).
 *
 * Usage example:
 *   await bootstrapGoogleMaps({ key: 'YOUR_API_KEY', version: 'weekly' });
 *   const { Map } = await google.maps.importLibrary('maps');
 */
export async function bootstrapGoogleMaps({
	key,
	version = 'weekly',
}: {
	key?: string;
	version?: string;
} = {}) {
	if (!window.google) {
		window.google = {} as unknown as typeof window.google;
	}

	if (!window.google.maps) {
		window.google.maps = {} as unknown as typeof window.google.maps;
	}
	const callbackName = '__ib__';
	const google = window.google;
	const maps = google.maps as typeof google.maps & {
		[callbackName]?: () => void;
	};

	// If the real importLibrary is already present, nothing to do.
	if (typeof maps.importLibrary === 'function') {
		return;
	}

	// Collect libraries requested before the script finishes loading.
	const queuedLibraries = new Set<string>();
	let scriptPromise: Promise<void>; // <script> load promise (singleton)

	/** Creates the <script> tag exactly once and returns a promise for it. */
	function ensureScript() {
		if (scriptPromise) return scriptPromise;

		scriptPromise = new Promise<void>((resolve, reject) => {
			// Build query-string parameters
			const qs = new URLSearchParams({
				key: getApiKey(key),
				v: version,
				libraries: Array.from(queuedLibraries).join(','),
				callback: `google.maps.${callbackName}`,
			});

			maps[callbackName] = resolve;

			// Inject the script
			const s = document.createElement('script');
			s.src = `https://maps.googleapis.com/maps/api/js?${qs}`;
			s.async = true;
			s.onerror = () =>
				reject(new Error('The Google Maps JavaScript API failed to load.'));
			document.head.appendChild(s);
		});

		return scriptPromise;
	}

	/**
	 * Stub version of google.maps.importLibrary.
	 * Queues requested libraries and waits until the real one arrives.
	 */
	maps.importLibrary = function stubImportLibrary(libName, ...args) {
		queuedLibraries.add(libName);
		return ensureScript().then(() => {
			// Ensure the real importLibrary has replaced our stub
			if (maps.importLibrary === stubImportLibrary) {
				throw new Error(
					'Google Maps API failed to load properly - importLibrary not available',
				);
			}
			return maps.importLibrary(libName, ...args);
		});
	};
	return ensureScript();
}
